import '@fontsource/ubuntu';
import '@fontsource/source-code-pro';
import '@fontsource/inter';
import React, { useCallback, useEffect, useState } from 'react';
import { useKeyPress } from 'react-use';
import { BsFillPersonFill } from 'react-icons/bs';
import { MdClose, MdSearch } from 'react-icons/md';
import { BiFilter } from 'react-icons/bi';
import { useFloorContext } from '../../context/FloorContext';
import { useSearchContext } from '../../context/SearchContext';
import SidePanelRoomPreview from './SidePanelRoomPreview';
import FloorSelector from './FloorSelector';
import SearchResultsPanel from './SearchResultsPanel';
import Filters from './Filters';
import SidePanelMyRooms from './SidePanelMyRooms';
import { RoomPlacingModel, RoomObjectType } from '../../types/room';
import { useNFTContext } from '../../context/NFTContext';
import PlaceRoomDialog from './dialogs/PlaceRoomDialog';
import ZoomButtons from './ZoomButtons';
import LandingPage from './LandingPage';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import OutsideAlerter from '../utils/OutsideAlerter';

const UIController = ({
  bringCameraToNFT,
  cameraMinusZoom,
  cameraPlusZoom,
  showLandingPage,
  setShowLandingPage,
}: {
  bringCameraToNFT: (nft: RoomObjectType) => void;
  cameraMinusZoom: () => void;
  cameraPlusZoom: () => void;
  showLandingPage: boolean;
  setShowLandingPage: (b: boolean) => void;
}) => {
  const [searchPanelVisible, setSearchPanelVisible] = useState(false);
  const [filterUIVisible, setFilterUIVisible] = useState(false);
  const [roomPreviewPanelVisible, setRoomPreviewPanelVisible] = useState(false);
  const [myRoomsPanelVisible, setMyRoomsPanelVisible] = useState(false);
  const [placeRoomDialogVisible, setPlaceRoomDialogVisible] = useState(false);

  const { currentFloor, changeCurrentFloor, totalFloors, isFetching } =
    useFloorContext();
  const { searchTerm, setSearchTerm, searchFetching, suggestedRooms } =
    useSearchContext();
  const {
    filteredNFTs,
    selectedNFT,
    nftBeingPlaced,
    setSelectedNFT,
    setFilteredNFTs,
    setNFTPlacingMode,
    setNFTPlacingCells,
    setNFTPlacingModel,
    setNFTBeingPlaced,
    setNFTPlacingValid,
  } = useNFTContext();

  const handleFloorChange = useCallback(
    (index: number) => {
      if (!isFetching) {
        setShowLandingPage(false);
        setSelectedNFT(undefined);
        changeCurrentFloor(index);
      }
    },
    [changeCurrentFloor, isFetching, setSelectedNFT, setShowLandingPage]
  );

  const handleStartPlacingNFT = useCallback(
    (nft: RoomObjectType) => {
      setShowLandingPage(false);
      setPlaceRoomDialogVisible(true);
      setNFTBeingPlaced(nft);
    },
    [setNFTBeingPlaced, setShowLandingPage]
  );

  const handleNavigateToSelectedNft = useCallback(
    (nft: RoomObjectType) => {
      if (
        nft.isPlaced &&
        nft.endX > -1 &&
        nft.startX > -1 &&
        nft.endY > -1 &&
        nft.startY > -1
      ) {
        if (currentFloor !== nft.roomFloor) {
          changeCurrentFloor(nft.roomFloor);
        }

        setShowLandingPage(false);
        setSearchTerm('');
        setFilterUIVisible(false);
        setSelectedNFT(nft);
        bringCameraToNFT(nft);
        setSearchPanelVisible(false);
      }
    },
    [
      bringCameraToNFT,
      changeCurrentFloor,
      currentFloor,
      setSearchTerm,
      setSelectedNFT,
      setShowLandingPage,
    ]
  );

  const handleAcceptPlacingNFT = useCallback(
    (model: RoomPlacingModel) => {
      setSearchTerm('');
      setSelectedNFT(undefined);
      setFilteredNFTs([]);
      setPlaceRoomDialogVisible(false);
      setNFTPlacingModel(model);
      setNFTPlacingMode(true);
      setMyRoomsPanelVisible(true);
    },
    [
      setFilteredNFTs,
      setNFTPlacingMode,
      setNFTPlacingModel,
      setSearchTerm,
      setSelectedNFT,
    ]
  );

  useEffect(() => {
    if (selectedNFT) {
      setRoomPreviewPanelVisible(true);
      setMyRoomsPanelVisible(false);
      setShowLandingPage(false);
    } else {
      setRoomPreviewPanelVisible(false);
    }
  }, [selectedNFT, setShowLandingPage]);

  const onRoomPreviewPanelClose = useCallback(() => {
    setSelectedNFT(undefined);
    setRoomPreviewPanelVisible(false);
  }, [setSelectedNFT]);

  const onMyRoomsClick = useCallback(() => {
    if (myRoomsPanelVisible === false) {
      setSearchTerm('');
      setRoomPreviewPanelVisible(false);
      setMyRoomsPanelVisible(true);
    } else {
      setMyRoomsPanelVisible(false);
      setNFTPlacingMode(false);
      setNFTPlacingValid(false);
      setNFTBeingPlaced(undefined);
      setNFTPlacingCells([]);
    }
  }, [
    myRoomsPanelVisible,
    setNFTBeingPlaced,
    setNFTPlacingCells,
    setNFTPlacingMode,
    setNFTPlacingValid,
    setSearchTerm,
  ]);

  const onFiltersClick = useCallback(() => {
    setFilterUIVisible(!filterUIVisible);
  }, [filterUIVisible]);

  const escapePressed = useKeyPress('Escape');
  useEffect(() => {
    setSearchTerm('');
    setMyRoomsPanelVisible(false);
    setSearchPanelVisible(false);
    setFilterUIVisible(false);
    setSelectedNFT(undefined);
    setNFTPlacingMode(false);
    setNFTBeingPlaced(undefined);
    setNFTPlacingCells([]);
  }, [
    escapePressed,
    setNFTBeingPlaced,
    setNFTPlacingCells,
    setNFTPlacingMode,
    setSearchTerm,
    setSelectedNFT,
  ]);

  return (
    <>
      {showLandingPage && (
        <LandingPage
          close={() => setShowLandingPage(false)}
          suggestedNFTS={suggestedRooms}
          collabNFTS={suggestedRooms}
        />
      )}
      <OutsideAlerter onOutsideClick={() => setSearchPanelVisible(false)}>
        <div className="search-ui-container">
          <div className="search-ui-wrapper">
            <input
              className="search-input"
              type="text"
              placeholder="Search for rooms..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              onSelect={() => setSearchPanelVisible(true)}
            />
            {searchTerm === '' ? (
              <MdSearch className="search-icon" size={20} />
            ) : searchFetching ? (
              <div className="search-fetching">
                <AiOutlineLoading3Quarters
                  color="white"
                  size={12}
                  className="loading-icon"
                />
              </div>
            ) : (
              <MdClose
                className="search-icon"
                size={20}
                onClick={() => setSearchTerm('')}
              />
            )}
          </div>
          <div className="filters-button" onClick={onFiltersClick}>
            Filters
            <BiFilter className="filters-icon" size={22} />
          </div>
          <div className="my-rooms-button-container">
            <BsFillPersonFill
              className="my-rooms-button"
              onClick={onMyRoomsClick}
            />
          </div>
          {filterUIVisible && <Filters />}
          {searchPanelVisible && (
            <SearchResultsPanel
              handleNavigateToSelectedNft={handleNavigateToSelectedNft}
              suggested={suggestedRooms}
              results={filteredNFTs}
              filtersVisible={filterUIVisible}
            />
          )}
        </div>
      </OutsideAlerter>

      {roomPreviewPanelVisible && (
        <div
          className={`side-panel-container ${filterUIVisible ? 'padded' : ''}`}
        >
          {selectedNFT && (
            <SidePanelRoomPreview
              selectedNFT={selectedNFT}
              handleStartPlacingNFT={handleStartPlacingNFT}
              onPanelClose={onRoomPreviewPanelClose}
            />
          )}
        </div>
      )}

      {myRoomsPanelVisible && (
        <div
          className={`side-panel-container ${filterUIVisible ? 'padded' : ''}`}
        >
          <SidePanelMyRooms
            handleStartPlacingNFT={handleStartPlacingNFT}
            handleNavigateToSelectedNft={handleNavigateToSelectedNft}
            onPanelClose={onMyRoomsClick}
          />
        </div>
      )}

      {placeRoomDialogVisible && nftBeingPlaced && (
        <PlaceRoomDialog
          nftBeingPlaced={nftBeingPlaced}
          handleAcceptPlacingNFT={handleAcceptPlacingNFT}
          onCancel={() => {
            setPlaceRoomDialogVisible(false);
          }}
        />
      )}

      {totalFloors > 1 ? (
        <FloorSelector
          handleFloorChange={handleFloorChange}
          showLandingPage={showLandingPage}
        />
      ) : (
        <div className="floor-selector-loading">
          <AiOutlineLoading3Quarters
            color="white"
            size={40}
            className="loading-icon"
          />
        </div>
      )}

      <ZoomButtons
        cameraMinusZoom={cameraMinusZoom}
        cameraPlusZoom={cameraPlusZoom}
      />
    </>
  );
};

export default UIController;
